import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Check, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "components/ui/button";
import { Calendar } from "components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Combobox } from "components/ui/combobox";
import { cn } from "lib/utils";
import { useAuth } from "app/provider";
import { downloadShipmentInformationReport } from "./api/index";

interface DateRange {
    from?: Date;
    to?: Date;
}

const STORAGE_KEY = "selectedCustId";

const ShipmentInformationReports = ({ embedded = false }: { embedded?: boolean }) => {
    const { userType, customers } = useAuth();

    const [date, setDate] = useState<DateRange>({});
    const [dateOpen, setDateOpen] = useState(false);
    const [dateTarget, setDateTarget] = useState<"from" | "to">("from");
    const [downloading, setDownloading] = useState(false);
    const [selectedCustId, setSelectedCustId] = useState<string | undefined>();

    useEffect(() => {
        if (!customers || customers.length === 0) {
            setSelectedCustId(undefined);
            return;
        }

        if (userType === "Internal") {
            const storedCustId = localStorage.getItem(STORAGE_KEY);
            const isStoredValid = storedCustId
                ? customers.some(c => c.cust_code === storedCustId)
                : false;

            const defaultCustId = isStoredValid
                ? storedCustId!
                : customers[0].cust_code;

            setSelectedCustId(defaultCustId);
            localStorage.setItem(STORAGE_KEY, defaultCustId);
            return;
        }

        setSelectedCustId(customers[0].cust_code);
    }, [userType, customers]);

    const customerOptions =
        customers
            ?.filter(c => c.cust_code)
            .map(c => ({
                value: c.cust_code,
                label: c.cust_name,
            })) || [];

    const currentCustomerName = customers?.find(c => c.cust_code === selectedCustId)?.cust_name || "-";

    const handleDateSelect = (selectedDate?: Date) => {
        if (!selectedDate) return;

        if (dateTarget === "from") {
            setDate(prev => ({
                ...prev,
                from: selectedDate,
                ...(prev.to && selectedDate > prev.to
                    ? { to: undefined }
                    : {}),
            }));

            setDateOpen(false);
            return;
        }

        setDate(prev => ({
            ...prev,
            to: selectedDate,
        }));

        setDateOpen(false);
    };

    const handleCustomerChange = (custId: string) => {
        const nextCustId = custId || undefined;
        setSelectedCustId(nextCustId);

        if (nextCustId) {
            localStorage.setItem(STORAGE_KEY, nextCustId);
        }
    };

    const handleDownload = async () => {
        if (!selectedCustId) {
            toast.error("Customer not found");
            return;
        }

        setDownloading(true);

        try {
            const blob = await downloadShipmentInformationReport(
                date.from,
                date.to,
                selectedCustId
            );

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;

            const fileDatePart =
                date.from && date.to
                    ? `${format(date.from, "dd-MM-yyyy")}_${format(date.to, "dd-MM-yyyy")}`
                    : date.from
                    ? `from_${format(date.from, "dd-MM-yyyy")}`
                    : date.to
                    ? `upto_${format(date.to, "dd-MM-yyyy")}`
                    : "all";

            link.download = `Shipment_Report_${fileDatePart}.xlsx`;

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);

            toast.success("Report downloaded successfully", {
                style: {
                    background: "#fff",
                    color: "#111827",
                    border: "1px solid #e5e7eb",
                },
                icon: (
                    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-green-100">
                        <Check className="w-3 h-3 text-green-600" />
                    </div>
                ),
            });
        } catch (error) {
            console.error("Error downloading shipment report:", error);
            toast.error("Failed to download report");
        } finally {
            setDownloading(false);
        }
    };

    if (embedded) {
        return (
            <>
                <div className="w-[240px] shrink-0">
                    <Popover open={dateOpen} onOpenChange={setDateOpen}>
                        <div className="flex items-center border rounded-md bg-background h-8 overflow-hidden">
                            <div className="flex divide-x w-full">
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        onClick={() => {
                                            setDateTarget("from");
                                            setDateOpen(true);
                                        }}
                                        className={cn(
                                            "h-8 px-2 justify-start text-left font-normal rounded-none rounded-l-md hover:bg-muted/50 text-xs min-w-[118px]",
                                            !date?.from && "text-gray-500"
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-3 w-3 flex-shrink-0" />

                                        <div className="flex flex-col min-w-0 leading-tight">
                                            <span className="text-[10px] text-gray-600">From Date</span>

                                            <span className="truncate">
                                                {date?.from ? format(date.from, "dd MMM yyyy") : ""}
                                            </span>
                                        </div>
                                    </Button>
                                </PopoverTrigger>

                                <PopoverTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        onClick={() => {
                                            setDateTarget("to");
                                            setDateOpen(true);
                                        }}
                                        className={cn(
                                            "h-8 px-2 justify-start text-left font-normal rounded-none rounded-r-md hover:bg-muted/50 text-xs min-w-[118px]",
                                            !date?.to && "text-gray-500"
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-3 w-3 flex-shrink-0" />

                                        <div className="flex flex-col min-w-0 leading-tight">
                                            <span className="text-[10px] text-gray-600">To Date</span>

                                            <span className="truncate">
                                                {date?.to ? format(date.to, "dd MMM yyyy") : ""}
                                            </span>
                                        </div>
                                    </Button>
                                </PopoverTrigger>
                            </div>
                        </div>

                        <PopoverContent className="w-fit p-0" align="start">
                            <div className="p-3">
                                <div className="text-sm font-semibold text-center mb-2">
                                    {dateTarget === "from" ? "Select start date" : "Select end date"}
                                </div>

                                <Calendar
                                    mode="single"
                                    selected={dateTarget === "from" ? date.from : date.to}
                                    onSelect={handleDateSelect}
                                    numberOfMonths={2}
                                    className="p-0"
                                    classNames={{
                                        months: "flex gap-3",
                                        month: "space-y-2",
                                        caption: "flex justify-center items-center text-sm",
                                        nav_button: "h-7 w-7",
                                        head_cell: "w-8 text-[11px]",
                                        cell: "h-8 w-8 p-0",
                                        day: "h-8 w-8 text-xs p-0",
                                    }}
                                    disabled={(d) => {
                                        if (dateTarget === "to" && date.from) {
                                            return d < date.from;
                                        }
                                        return false;
                                    }}
                                />

                                <div className="flex justify-between mt-2 pt-2 border-t">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                        onClick={() => setDate({})}
                                    >
                                        Clear
                                    </Button>

                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-8 px-2 text-xs"
                                        onClick={() => setDateOpen(false)}
                                    >
                                        Close
                                    </Button>
                                </div>
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>

                <div className="w-[200px] shrink-0">
                    {userType === "Internal" ? (
                        <Combobox
                            options={customerOptions}
                            value={selectedCustId || ""}
                            onValueChange={handleCustomerChange}
                            placeholder="Customer"
                            searchPlaceholder="Search customer..."
                            emptyText="No customer found."
                            buttonClassName="h-8 text-xs w-full"
                        />
                    ) : (
                        <div className="h-8 flex items-center text-xs text-slate-700 truncate">{currentCustomerName}</div>
                    )}
                </div>

                <div className="w-[140px] shrink-0">
                    <Button
                        onClick={handleDownload}
                        disabled={(!date.from && !date.to) || downloading || !selectedCustId}
                        className="h-8 w-full text-xs"
                    >
                        <Download className="h-4 w-4 mr-2" />
                        {downloading ? "..." : "Download"}
                    </Button>
                </div>
            </>
        );
    }
};

export default ShipmentInformationReports;